import { Box, Heading, Icon, keyframes, Stack, Text } from '@chakra-ui/react'
import { FC, ReactNode } from 'react'
import { BsCameraVideo } from 'react-icons/bs'

// "Live" pulse: rings radiate out from the circle to signal an active connection.
const pulseRing = keyframes`
  0%   { transform: scale(0.85); opacity: 0.55; }
  80%  { opacity: 0; }
  100% { transform: scale(2); opacity: 0; }
`
// Gentle breathing on the circle itself.
const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
`
// Blinking green "live" dot.
const liveBlink = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
`

interface CuideiLiveCardProps {
  title: string
  description: string
  /** Green "live" status label shown under the description. */
  statusLabel: string
  /** Optional extra content (e.g. waiting timer / device-check note). */
  note?: ReactNode
  /** Top margin override (used to pull the band up against the header). */
  mt?: Record<string, number | string> | number | string
  /** Bottom margin override (used to abut the footer). */
  mb?: Record<string, number | string> | number | string
}

/**
 * Full-bleed teal→blue gradient band with a centered white card containing a
 * pulsing "live" video circle. Used both as the landing live-preview section and
 * as the actual waiting screen (StepFilaVideo). Breaks out of the shell
 * Container to span the full viewport width.
 */
const CuideiLiveCard: FC<CuideiLiveCardProps> = ({ title, description, statusLabel, note, mt, mb }) => (
  <Box
    position="relative"
    left="50%"
    right="50%"
    mx="-50vw"
    w="100vw"
    mt={mt}
    mb={mb}
    bgGradient="linear(to-br, #2BB8A6 0%, #3A7DEE 45%, #2F62E6 100%)"
    display="flex"
    alignItems="center"
    justifyContent="center"
    px={4}
    py={{ base: 16, md: 24 }}
    overflow="hidden"
  >
    {/* subtle dot texture */}
    <Box
      position="absolute"
      inset={0}
      opacity={0.5}
      backgroundImage="radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)"
      backgroundSize="22px 22px"
      pointerEvents="none"
    />

    <Box
      position="relative"
      bg="white"
      borderRadius="3xl"
      boxShadow="2xl"
      p={{ base: 8, md: 12 }}
      maxW="lg"
      w="full"
      textAlign="center"
    >
      <Stack align="center" spacing={6}>
        {/* Pulsing "live" video circle with radiating wave rings */}
        <Box position="relative" w={24} h={24}>
          {[0, 1].map((i) => (
            <Box
              key={i}
              position="absolute"
              inset={0}
              borderRadius="full"
              bgGradient="linear(to-br, cuidei.teal, cuidei.60)"
              animation={`${pulseRing} 2.4s ease-out infinite`}
              sx={{ animationDelay: `${i * 1.2}s` }}
            />
          ))}
          <Box
            position="relative"
            w="full"
            h="full"
            borderRadius="full"
            bgGradient="linear(to-br, cuidei.teal, cuidei.60)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="0 12px 30px rgba(47,107,237,0.45)"
            animation={`${breathe} 3s ease-in-out infinite`}
          >
            <Icon as={BsCameraVideo} color="white" fontSize="4xl" />
          </Box>
        </Box>

        <Stack align="center" spacing={2}>
          <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} color="cuidei.ink">
            {title}
          </Heading>
          <Text color="cuidei.muted" maxW="sm">
            {description}
          </Text>
        </Stack>

        <Stack direction="row" align="center" spacing={2}>
          <Box
            w={2.5}
            h={2.5}
            borderRadius="full"
            bg="green.400"
            animation={`${liveBlink} 1.4s ease-in-out infinite`}
          />
          <Text fontWeight={600} color="green.500" fontSize="sm">
            {statusLabel}
          </Text>
        </Stack>

        {note}
      </Stack>
    </Box>
  </Box>
)

export default CuideiLiveCard
